import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  Truck, Edit, Trash2, Eye, XCircle,
  LayoutGrid, MapPin, Clock, Target, Flag, User, AlertTriangle,
  CheckCircle, Navigation, PackageCheck, Layers, Package, DollarSign,
} from 'lucide-react';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import useColumnHeaderFilters from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import localApiAdapter from '../../core/services/localApiAdapter';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import { CustomSelect } from '../../shared/components/custom';
import '../../shared/components/custom/CustomComponents.css';
import VentesListPageChrome, { facturationTableScrollClass } from './components/VentesListPageChrome';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import useModuleConfigTheme from '../../shared/hooks/useModuleConfigTheme';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import QawaemErpCrudModal, { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import {
  mapVentesStatCards,
  useCrudModalBodyLock,
  initVentesColumnVisibility,
  buildVentesQawaemColumnDefs,
  isVentesColumnVisible,
  buildVentesBulkDeleteFilterAction,
  computeVentesRowSelection,
  resolveVentesEntityReference,
  useVentesEntityReference,
  VentesConfigNotice,
  sliceVentesListItems,
  matchesVentesListDateRange,
} from './ventesListPageUtils';
import { useVentesConfig } from './useVentesConfig';
import { useSettings } from '../../core/contexts/SettingsContext';
import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
import {
  EMPTY_DELIVERY_FORM,
  mapDeliveryToForm,
  buildDeliveryPayload,
  getDeliveryFormSections,
  DELIVERY_VIEW_COLUMNS,
} from './livraisonsVentesForm';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';

const LivraisonsVentesPage = () => {
  const { t } = useTranslation();
  const { isDashboardLightMode } = useGlobalTheme();
  const cfgTheme = useModuleConfigTheme();
  const isLight = isDashboardLightMode ?? cfgTheme.light;
  const qawaem = isSaudiBrand();
  const p = (key, opts) => t(`modules.ventes.pages.livraisonsVentesPage.${key}`, opts);

  const getStatusLabel = (status) => {
    const map = {
      pending: p('status_pending'),
      scheduled: p('status_scheduled'),
      in_transit: p('status_in_transit'),
      delivered: p('status_delivered'),
      failed: p('status_failed'),
      cancelled: p('status_cancelled'),
      on_hold: p('status_on_hold'),
      returned: p('status_returned'),
    };
    return map[status] || status || p('em_dash');
  };

  const getPriorityLabel = (priority) => {
    const map = {
      low: p('priority_low'),
      medium: p('priority_medium'),
      high: p('priority_high'),
      critical: p('priority_critical'),
    };
    return map[priority] || priority || p('em_dash');
  };

  const getZoneLabel = (zone) => {
    const map = {
      north: p('zone_north'),
      south: p('zone_south'),
      east: p('zone_east'),
      west: p('zone_west'),
      center: p('zone_center'),
    };
    return map[zone] || zone || p('em_dash');
  };

  const getTypeLabel = (type) => {
    const map = {
      standard: p('type_standard'),
      express: p('type_express'),
      same_day: p('type_same_day'),
      scheduled: p('type_scheduled'),
    };
    return map[type] || type || p('em_dash');
  };

  const [searchTerm, setSearchTerm] = useState('');
  const [deliveries, setDeliveries] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingDelivery, setEditingDelivery] = useState(null);
  const [selectedDeliveries, setSelectedDeliveries] = useState([]);
  const [viewMode, setViewMode] = useState('table');
  const ventesConfig = useVentesConfig();
  const { dateRange } = useSaudiListDateRange();

  useEffect(() => {
    setViewMode(ventesConfig.defaultView || 'table');
  }, [ventesConfig.defaultView]);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingDelivery, setViewingDelivery] = useState(null);
  const [saving, setSaving] = useState(false);

  useCrudModalBodyLock(showModal, showViewModal);
  const { settings } = useSettings();
  const generateDeliveryRef = useVentesEntityReference('delivery', deliveries);

  const [formTab, setFormTab] = useState('general');

  const [formData, setFormData] = useState({ ...EMPTY_DELIVERY_FORM });
  const [availableClients, setAvailableClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);

  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_deliveries');
  const [quickFilter, setQuickFilter] = useState('all');
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  
  const { filters, setFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const thFilterClass = 'text-white font-semibold py-4';

  const tableColumnDefs = useMemo(
    () => [
      { key: 'delivery_reference', label: p('col_reference'), required: true },
      { key: 'customer_name', label: p('col_customer') },
      { key: 'delivery_date', label: p('col_date') },
      { key: 'delivery_city', label: p('col_city') },
      { key: 'status', label: p('col_status') },
    ],
    [p],
  );
  const [columnVisibility, setColumnVisibility] = useState(() =>
    initVentesColumnVisibility(tableColumnDefs.map((c) => c.key)),
  );
  const qawaemColumnDefs = useMemo(
    () => buildVentesQawaemColumnDefs(tableColumnDefs, columnVisibility),
    [tableColumnDefs, columnVisibility],
  );
  const handleToggleColumn = (key) => {
    setColumnVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;
  const showCol = (key) => isVentesColumnVisible(columnVisibility, key);
  const tableColSpan = tableColumnDefs.filter((c) => showCol(c.key)).length + 2;

  useEffect(() => { loadDeliveries(); }, []);

  useEffect(() => {
    if (!showModal) return;
    const loadClients = async () => {
      try {
        setLoadingClients(true);
        const response = await localApiAdapter.get('/facturation/clients');
        const rows = response?.data || response?.clients || response || [];
        setAvailableClients(Array.isArray(rows) ? rows : []);
      } catch (error) {
        console.error('Error loading clients:', error);
        setAvailableClients([]);
      } finally {
        setLoadingClients(false);
      }
    };
    void loadClients();
  }, [showModal]);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadDeliveries = async () => {
    try {
      const response = await localApiAdapter.get('/ventes/livraisons');
      const rows = response?.data || response || [];
      setDeliveries(sortRecordsNewestFirst(Array.isArray(rows) ? rows : []));
    } catch (error) {
      console.error('Error loading deliveries:', error);
      setDeliveries([]);
    }
  };

  const stats = {
    total: deliveries.length,
    pending: deliveries.filter(d => d.status === 'pending').length,
    inTransit: deliveries.filter(d => d.status === 'in_transit').length,
    delivered: deliveries.filter(d => d.status === 'delivered').length,
    failed: deliveries.filter(d => d.status === 'failed').length,
    cancelled: deliveries.filter(d => d.status === 'cancelled').length,
    scheduled: deliveries.filter(d => d.status === 'scheduled').length,
    onHold: deliveries.filter(d => d.status === 'on_hold').length,
    avgDeliveryTime: deliveries.filter(d => d.actual_duration).length > 0 
      ? (deliveries.filter(d => d.actual_duration).reduce((sum, d) => sum + (parseFloat(d.actual_duration) || 0), 0) / deliveries.filter(d => d.actual_duration).length).toFixed(1) 
      : 0,
    totalDistance: deliveries.reduce((sum, d) => sum + (parseFloat(d.distance_km) || 0), 0).toFixed(1),
    totalCost: deliveries.reduce((sum, d) => sum + (parseFloat(d.total_cost) || 0), 0),
    totalRevenue: deliveries.reduce((sum, d) => sum + (parseFloat(d.cod_amount) || 0) + (parseFloat(d.delivery_cost) || 0), 0),
    successRate: deliveries.length > 0 
      ? ((deliveries.filter(d => d.status === 'delivered').length / deliveries.length) * 100).toFixed(1) 
      : 0,
    onTimeRate: deliveries.filter(d => d.sla_met === 'yes').length > 0 
      ? ((deliveries.filter(d => d.sla_met === 'yes').length / deliveries.length) * 100).toFixed(1) 
      : 0,
    avgRating: deliveries.filter(d => d.rating).length > 0 
      ? (deliveries.filter(d => d.rating).reduce((sum, d) => sum + (parseFloat(d.rating) || 0), 0) / deliveries.filter(d => d.rating).length).toFixed(1) 
      : 0,
    totalPackages: deliveries.reduce((sum, d) => sum + (parseInt(d.package_count) || 0), 0),
    totalWeight: deliveries.reduce((sum, d) => sum + (parseFloat(d.total_weight) || 0), 0).toFixed(1),
    activeDrivers: [...new Set(deliveries.filter(d => d.driver_name).map(d => d.driver_name))].length,
    activeVehicles: [...new Set(deliveries.filter(d => d.vehicle_number).map(d => d.vehicle_number))].length,
    averageDelay: deliveries.filter(d => d.delay_minutes).length > 0 
      ? (deliveries.filter(d => d.delay_minutes).reduce((sum, d) => sum + (parseFloat(d.delay_minutes) || 0), 0) / deliveries.filter(d => d.delay_minutes).length).toFixed(1) 
      : 0
  };

  const mainTabs = useMemo(() => [
    { id: 'all_deliveries', name: p('tab_general'), icon: LayoutGrid },
    { id: 'par_statut', name: p('tab_by_status'), icon: Target },
    { id: 'par_zone', name: p('tab_by_zone'), icon: MapPin },
    { id: 'par_priorite', name: p('tab_by_priority'), icon: Flag },
    { id: 'par_type', name: p('tab_by_type'), icon: Truck },
    { id: 'par_chauffeur', name: p('tab_by_driver'), icon: User },
  ], [t]);

  const quickFilterButtons = useMemo(() => [
    { id: 'all', name: p('filter_all'), icon: Layers },
    { id: 'pending', name: p('filter_pending'), icon: Clock },
    { id: 'in_transit', name: p('filter_in_transit'), icon: Truck },
    { id: 'delivered', name: p('filter_delivered'), icon: CheckCircle },
    { id: 'failed', name: p('filter_failed'), icon: XCircle },
    { id: 'urgent', name: p('filter_urgent'), icon: AlertTriangle },
  ], [t]);

  const deliveryDistribution = useMemo(() => ({
    all_deliveries: {
      title: p('overview_title'),
      data: [
        { filterKey: 'pending', name: p('dist_pending'), count: stats.pending, color: 'bg-gradient-to-r from-yellow-500 to-yellow-600' },
        { filterKey: 'in_transit', name: p('dist_in_transit'), count: stats.inTransit, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'delivered', name: p('dist_delivered'), count: stats.delivered, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'failed', name: p('dist_failed'), count: stats.failed, color: 'bg-gradient-to-r from-red-500 to-red-600' },
        { filterKey: 'cancelled', name: p('dist_cancelled'), count: stats.cancelled, color: 'bg-gradient-to-r from-gray-500 to-gray-600' },
        { filterKey: 'scheduled', name: p('dist_scheduled'), count: stats.scheduled, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
      ],
    },
    par_statut: {
      title: p('dist_by_status_title'),
      data: [
        { filterKey: 'pending', name: p('dist_pending'), count: stats.pending, color: 'bg-gradient-to-r from-yellow-500 to-yellow-600' },
        { filterKey: 'in_transit', name: p('dist_in_transit'), count: stats.inTransit, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'delivered', name: p('dist_delivered'), count: stats.delivered, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'failed', name: p('dist_failed'), count: stats.failed, color: 'bg-gradient-to-r from-red-500 to-red-600' },
        { filterKey: 'cancelled', name: p('dist_cancelled'), count: stats.cancelled, color: 'bg-gradient-to-r from-gray-500 to-gray-600' },
        { filterKey: 'on_hold', name: p('dist_on_hold'), count: stats.onHold, color: 'bg-gradient-to-r from-orange-500 to-orange-600' },
      ],
    },
    par_zone: {
      title: p('dist_by_zone_title'),
      data: [
        { filterKey: 'north', name: p('zone_north'), count: deliveries.filter(d => d.zone === 'north').length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'south', name: p('zone_south'), count: deliveries.filter(d => d.zone === 'south').length, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'east', name: p('zone_east'), count: deliveries.filter(d => d.zone === 'east').length, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
        { filterKey: 'west', name: p('zone_west'), count: deliveries.filter(d => d.zone === 'west').length, color: 'bg-gradient-to-r from-orange-500 to-orange-600' },
        { filterKey: 'center', name: p('zone_center'), count: deliveries.filter(d => d.zone === 'center').length, color: 'bg-gradient-to-r from-pink-500 to-pink-600' },
      ],
    },
    par_priorite: {
      title: p('dist_by_priority_title'),
      data: [
        { filterKey: 'critical', name: p('priority_critical'), count: deliveries.filter(d => d.priority === 'critical').length, color: 'bg-gradient-to-r from-red-500 to-red-600' },
        { filterKey: 'high', name: p('priority_high'), count: deliveries.filter(d => d.priority === 'high').length, color: 'bg-gradient-to-r from-orange-500 to-orange-600' },
        { filterKey: 'medium', name: p('priority_medium'), count: deliveries.filter(d => d.priority === 'medium').length, color: 'bg-gradient-to-r from-yellow-500 to-yellow-600' },
        { filterKey: 'low', name: p('priority_low'), count: deliveries.filter(d => d.priority === 'low').length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
      ],
    },
    par_type: {
      title: p('dist_by_type_title'),
      data: [
        { filterKey: 'standard', name: p('type_standard'), count: deliveries.filter(d => d.delivery_type === 'standard').length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'express', name: p('type_express'), count: deliveries.filter(d => d.delivery_type === 'express').length, color: 'bg-gradient-to-r from-red-500 to-red-600' },
        { filterKey: 'same_day', name: p('type_same_day'), count: deliveries.filter(d => d.delivery_type === 'same_day').length, color: 'bg-gradient-to-r from-orange-500 to-orange-600' },
        { filterKey: 'scheduled', name: p('type_scheduled'), count: deliveries.filter(d => d.delivery_type === 'scheduled').length, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
      ],
    },
    par_chauffeur: {
      title: p('dist_by_driver_title'),
      data: (() => {
        const driverCounts = {};
        deliveries.forEach(d => {
          const driver = d.driver_name || '__unassigned__';
          driverCounts[driver] = (driverCounts[driver] || 0) + 1;
        });
        return Object.entries(driverCounts)
          .slice(0, 6)
          .map(([name, count], idx) => ({
            filterKey: name,
            name: name === '__unassigned__' ? p('driver_unassigned') : name,
            count,
            color: ['from-blue-500 to-blue-600', 'from-green-500 to-green-600', 'from-purple-500 to-purple-600', 'from-orange-500 to-orange-600', 'from-pink-500 to-pink-600', 'from-teal-500 to-teal-600'][idx] ? `bg-gradient-to-r ${['from-blue-500 to-blue-600', 'from-green-500 to-green-600', 'from-purple-500 to-purple-600', 'from-orange-500 to-orange-600', 'from-pink-500 to-pink-600', 'from-teal-500 to-teal-600'][idx]}` : 'bg-gradient-to-r from-blue-500 to-blue-600',
          }));
      })(),
    },
  }), [deliveries, stats, t]);

    const distributionInfo = deliveryDistribution[mainViewMode] || deliveryDistribution.all_deliveries;

  const deliveryStatusFilterOptions = useMemo(() => ([
    { value: 'pending', label: p('filter_pending') },
    { value: 'in_transit', label: p('filter_in_transit') },
    { value: 'delivered', label: p('filter_delivered') },
    { value: 'failed', label: p('filter_failed') },
    { value: 'cancelled', label: p('filter_cancelled') },
    { value: 'scheduled', label: p('dist_scheduled') },
    { value: 'on_hold', label: p('filter_on_hold') },
  ]), [p]);

  const columnFilterRules = useMemo(() => ([
    textColumnRule('delivery_reference'),
    textColumnRule('customer_name'),
    textColumnRule('delivery_date', (r) => r.delivery_date || ''),
    exactColumnRule('status', (r) => r.status ?? ''),
    textColumnRule('delivery_city'),
  ]), []);

  const baseFilteredDeliveries = useMemo(() => deliveries.filter(d => {
    if (!matchesVentesListDateRange(d.delivery_date, dateRange)) return false;
    // Search filter
    if (searchTerm && 
        !d.delivery_reference?.toLowerCase().includes(searchTerm.toLowerCase()) && 
        !d.tracking_number?.toLowerCase().includes(searchTerm.toLowerCase()) && 
        !d.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !d.driver_name?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    
    // Quick / secondary status filter
    if (quickFilter !== 'all') {
      if (quickFilter === 'pending' && d.status !== 'pending') return false;
      if (quickFilter === 'in_transit' && d.status !== 'in_transit') return false;
      if (quickFilter === 'delivered' && d.status !== 'delivered') return false;
      if (quickFilter === 'failed' && d.status !== 'failed') return false;
      if (quickFilter === 'urgent' && d.priority !== 'critical' && d.priority !== 'high') return false;
    }
    
    // Distribution card filter (clickable stats)
    if (activeDistributionFilter) {
      switch (mainViewMode) {
        case 'all_deliveries':
          if (activeDistributionFilter === 'pending' && d.status !== 'pending') return false;
          if (activeDistributionFilter === 'in_transit' && d.status !== 'in_transit') return false;
          if (activeDistributionFilter === 'delivered' && d.status !== 'delivered') return false;
          if (activeDistributionFilter === 'failed' && d.status !== 'failed') return false;
          if (activeDistributionFilter === 'cancelled' && d.status !== 'cancelled') return false;
          if (activeDistributionFilter === 'scheduled' && d.status !== 'scheduled') return false;
          break;
        case 'par_statut':
          if (activeDistributionFilter === 'pending' && d.status !== 'pending') return false;
          if (activeDistributionFilter === 'in_transit' && d.status !== 'in_transit') return false;
          if (activeDistributionFilter === 'delivered' && d.status !== 'delivered') return false;
          if (activeDistributionFilter === 'failed' && d.status !== 'failed') return false;
          if (activeDistributionFilter === 'cancelled' && d.status !== 'cancelled') return false;
          if (activeDistributionFilter === 'on_hold' && d.status !== 'on_hold') return false;
          break;
        case 'par_zone':
          if (activeDistributionFilter === 'north' && d.zone !== 'north') return false;
          if (activeDistributionFilter === 'south' && d.zone !== 'south') return false;
          if (activeDistributionFilter === 'east' && d.zone !== 'east') return false;
          if (activeDistributionFilter === 'west' && d.zone !== 'west') return false;
          if (activeDistributionFilter === 'center' && d.zone !== 'center') return false;
          break;
        case 'par_priorite':
          if (activeDistributionFilter === 'critical' && d.priority !== 'critical') return false;
          if (activeDistributionFilter === 'high' && d.priority !== 'high') return false;
          if (activeDistributionFilter === 'medium' && d.priority !== 'medium') return false;
          if (activeDistributionFilter === 'low' && d.priority !== 'low') return false;
          break;
        case 'par_type':
          if (activeDistributionFilter === 'standard' && d.delivery_type !== 'standard') return false;
          if (activeDistributionFilter === 'express' && d.delivery_type !== 'express') return false;
          if (activeDistributionFilter === 'same_day' && d.delivery_type !== 'same_day') return false;
          if (activeDistributionFilter === 'scheduled' && d.delivery_type !== 'scheduled') return false;
          break;
        case 'par_chauffeur':
          if (activeDistributionFilter === '__unassigned__' && d.driver_name) return false;
          if (activeDistributionFilter !== '__unassigned__' && d.driver_name !== activeDistributionFilter) return false;
          break;
      }
    }
    
    return true;
  }), [deliveries, quickFilter, searchTerm, activeDistributionFilter, mainViewMode, dateRange]);

  const filteredDeliveries = useMemo(
    () => applyColumnFilters(baseFilteredDeliveries, filters, columnFilterRules),
    [baseFilteredDeliveries, filters, columnFilterRules],
  );

  const filteredDeliveryIds = useMemo(() => filteredDeliveries.map((d) => d.id), [filteredDeliveries]);
  const displayedDeliveries = useMemo(
    () => sliceVentesListItems(filteredDeliveries, ventesConfig.itemsPerPage),
    [filteredDeliveries, ventesConfig.itemsPerPage],
  );
  const { allRowsSelected, someRowsSelected } = useMemo(
    () => computeVentesRowSelection(filteredDeliveryIds, selectedDeliveries),
    [filteredDeliveryIds, selectedDeliveries],
  );

  const openCreateModal = () => {
    if (!ventesConfig.enableDeliveries) return;
    setEditingDelivery(null);
    setFormData({
      ...EMPTY_DELIVERY_FORM,
      delivery_reference: ventesConfig.autoGenerateDeliveryReference !== false ? generateDeliveryRef() : '',
      signature_required: ventesConfig.signatureRequired !== false ? 'yes' : 'no',
    });
    setFormTab('general');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (ventesConfig.requireCustomer !== false && !formData.customer_id && !formData.customer_name) {
      notifyError(showNotify, p('alert_customer_required', { defaultValue: 'يرجى اختيار العميل' }));
      return;
    }
    try {
      setSaving(true);
      const resolvedForm = {
        ...formData,
        delivery_reference: resolveVentesEntityReference(
          settings,
          'delivery',
          deliveries,
          formData.delivery_reference,
        ),
      };
      const submitData = buildDeliveryPayload(resolvedForm);
      if (editingDelivery?.id) {
        await localApiAdapter.put(`/ventes/livraisons/${editingDelivery.id}`, submitData);
        notifyUpdated(showNotify, p('entity_label', { defaultValue: 'تسليم' }));
      } else {
        await localApiAdapter.post('/ventes/livraisons', submitData);
        notifyCreated(showNotify, p('entity_label', { defaultValue: 'تسليم' }));
      }
      resetForm();
      await loadDeliveries();
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, `${p('alert_save_error')}: ${error.message || p('alert_unknown_error')}`);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (delivery) => {
    setFormTab('general');
    setEditingDelivery(delivery);
    setFormData(mapDeliveryToForm(delivery));
    setShowModal(true);
  };

  const handleView = (delivery) => {
    setViewingDelivery(delivery);
    setShowViewModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(p('delete_confirm'))) return;
    try {
      await localApiAdapter.delete(`/ventes/livraisons/${id}`);
      notifyDeleted(showNotify, p('entity_label', { defaultValue: 'تسليم' }));
      await loadDeliveries();
    } catch (error) {
      console.error('Error:', error);
      notifyError(showNotify, p('alert_delete_error', { defaultValue: 'تعذّر حذف التسليم' }));
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(p('bulk_delete_confirm', { count: selectedDeliveries.length }))) return;
    try {
      const deleteCount = selectedDeliveries.length;
      await Promise.all(selectedDeliveries.map((id) => localApiAdapter.delete(`/ventes/livraisons/${id}`)));
      notifyBulkDeleted(showNotify, deleteCount, p('entity_label_plural', { defaultValue: 'تسليم' }));
      setSelectedDeliveries([]);
      await loadDeliveries();
    } catch (error) {
      console.error('Error deleting deliveries:', error);
      notifyError(showNotify, p('alert_bulk_delete_error', { defaultValue: 'تعذّر حذف التسليمات المحددة' }));
    }
  };

  const resetForm = () => {
    setFormData({ ...EMPTY_DELIVERY_FORM });
    setEditingDelivery(null);
    setFormTab('general');
    setShowModal(false);
  };
  const deliveryExportHeaders = () => ([
    { key: 'delivery_reference', label: p('col_reference') },
    { key: 'tracking_number', label: p('col_tracking') },
    { key: 'customer_name', label: p('col_customer') },
    { key: 'delivery_city', label: p('col_city') },
    { key: 'driver_name', label: p('col_driver') },
    { key: 'status', label: p('col_status') },
    { key: 'delivery_date', label: p('col_delivery_date') },
  ]);

  const handleExportCSV = () => {
    const headers = deliveryExportHeaders();
    const fileName = `livraisons_ventes_${new Date().toISOString().split('T')[0]}.csv`;
    exportToCSV(filteredDeliveries, headers, fileName);
  };

  const handleExportExcel = () => {
    const headers = deliveryExportHeaders();
    const fileName = `livraisons_ventes_${new Date().toISOString().split('T')[0]}.xlsx`;
    void exportToExcel(filteredDeliveries, headers, fileName);
  };

  const handleExportPDF = async () => {
    const headers = deliveryExportHeaders();
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = `
      <div class="report-content">
        <table>
          <thead><tr>${headers.map((h) => `<th>${h.label}</th>`).join('')}</tr></thead>
          <tbody>
            ${filteredDeliveries.map((item) => `
              <tr>${headers.map((h) => `<td>${item[h.key] ?? ''}</td>`).join('')}</tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    `;
    document.body.appendChild(tempDiv);
    try {
      await generateReportPDF(p('title'), {});
    } finally {
      document.body.removeChild(tempDiv);
    }
  };

  const handleExport = () => handleExportExcel();

  const handleImport = () => {
    const headers = [
      { key: 'delivery_reference', label: p('col_reference') },
      { key: 'customer_name', label: p('col_customer') },
      { key: 'delivery_address', label: p('col_address') },
      { key: 'delivery_city', label: p('col_city') }
    ];
    const schema = {
      requiredFields: ['delivery_reference', 'customer_name'],
      validators: {
        total_cost: (value) => !value || !isNaN(parseFloat(value))
      }
    };
    triggerFileInput(async (file) => {
      try {
        const result = await importFromFile(file, headers, schema);
        if (result.success) {
          let successCount = 0;
          for (const record of result.data) {
            try {
              await localApiAdapter.post('/ventes/livraisons', record);
              successCount++;
            } catch (err) {
              console.error('Import record error:', err);
            }
          }
          await loadDeliveries();
          alert(p('import_success', { total: result.totalCount, imported: successCount }));
        }
      } catch (error) {
        console.error('Import error:', error);
        alert(p('alert_import_error'));
      }
    }, '.csv,.xlsx,.xls');
  };

  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) {
      setSelectedDeliveries([]);
    } else {
      setSelectedDeliveries(filteredDeliveryIds);
    }
  };
  
  const handleSelectDelivery = (id) => { 
    setSelectedDeliveries(selectedDeliveries.includes(id) ? selectedDeliveries.filter(dId => dId !== id) : [...selectedDeliveries, id]); 
  };

  const deliveryStatusOptions = useMemo(
    () => [
      { value: 'pending', label: p('status_pending'), icon: Clock, style: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' },
      { value: 'scheduled', label: p('status_scheduled'), icon: Target, style: 'bg-purple-500/20 text-purple-300 border-purple-500/30' },
      { value: 'in_transit', label: p('status_in_transit'), icon: Navigation, style: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
      { value: 'delivered', label: p('status_delivered'), icon: CheckCircle, style: 'bg-green-500/20 text-green-300 border-green-500/30' },
      { value: 'failed', label: p('status_failed'), icon: AlertTriangle, style: 'bg-red-500/20 text-red-300 border-red-500/30' },
      { value: 'cancelled', label: p('status_cancelled'), icon: XCircle, style: 'bg-gray-500/20 text-gray-300 border-gray-500/30' },
      { value: 'on_hold', label: p('status_on_hold'), icon: Flag, style: 'bg-orange-500/20 text-orange-300 border-orange-500/30' },
      { value: 'returned', label: p('status_returned'), icon: PackageCheck, style: 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30' },
    ],
    [t],
  );

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.put(`/ventes/livraisons/${recordId}`, { status: newStatus });
      await loadDeliveries();
    } catch (error) {
      console.error('Error updating delivery status:', error);
    }
  };

  const getPriorityBadge = (priority) => {
    const priorityConfig = {
      low: { style: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
      medium: { style: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' },
      high: { style: 'bg-orange-500/20 text-orange-300 border-orange-500/30' },
      critical: { style: 'bg-red-500/20 text-red-300 border-red-500/30' }
    };
    const config = priorityConfig[priority] || priorityConfig.medium;
    return <span className={`px-3 py-1.5 rounded-full text-xs font-medium border ${config.style}`}>{getPriorityLabel(priority)}</span>;
  };

  // Delivery type options for CustomSelect
  const deliveryTypeOptions = [
    { value: 'standard', label: p('type_standard') },
    { value: 'express', label: p('type_express') },
    { value: 'same_day', label: p('type_same_day') },
    { value: 'scheduled', label: p('type_scheduled') },
    { value: 'overnight', label: p('type_overnight') },
    { value: 'two_hour', label: p('type_two_hour') }
  ];

  const deliveryMethodOptions = [
    { value: 'truck', label: p('method_truck') },
    { value: 'van', label: p('method_van') },
    { value: 'bike', label: p('method_bike') },
    { value: 'car', label: p('method_car') },
    { value: 'drone', label: p('method_drone') },
    { value: 'walking', label: p('method_walking') }
  ];

  const statusOptions = deliveryStatusOptions.map(({ value, label }) => ({ value, label }));

  const priorityOptions = [
    { value: 'low', label: p('priority_low') },
    { value: 'medium', label: p('priority_medium') },
    { value: 'high', label: p('priority_high') },
    { value: 'critical', label: p('priority_critical') }
  ];

  const yesNoOptions = [
    { value: 'yes', label: p('yes') },
    { value: 'no', label: p('no') }
  ];

  const paymentMethodOptions = [
    { value: 'cash', label: p('payment_cash') },
    { value: 'mada', label: p('payment_mada') },
    { value: 'card', label: p('payment_card') },
    { value: 'cod', label: p('payment_cod') },
    { value: 'transfer', label: p('payment_transfer') },
    { value: 'cheque', label: p('payment_cheque') },
    { value: 'online', label: p('payment_online') },
  ];

  const paymentStatusOptions = [
    { value: 'pending', label: p('pay_status_pending') },
    { value: 'paid', label: p('pay_status_paid') },
    { value: 'partial', label: p('pay_status_partial') },
    { value: 'refunded', label: p('pay_status_refunded') },
    { value: 'failed', label: p('pay_status_failed') },
  ];

  const zoneOptions = [
    { value: 'north', label: p('zone_north') },
    { value: 'south', label: p('zone_south') },
    { value: 'east', label: p('zone_east') },
    { value: 'west', label: p('zone_west') },
    { value: 'center', label: p('zone_center') }
  ];

  const serviceLevelOptions = [
    { value: 'standard', label: p('service_standard') },
    { value: 'premium', label: p('service_premium') },
    { value: 'economy', label: p('service_economy') },
    { value: 'vip', label: p('service_vip') },
  ];

  const packageTypeOptions = [
    { value: 'parcel', label: p('package_parcel') },
    { value: 'envelope', label: p('package_envelope') },
    { value: 'pallet', label: p('package_pallet') },
    { value: 'box', label: p('package_box') },
    { value: 'bag', label: p('package_bag') },
    { value: 'container', label: p('package_container') }
  ];

  const vehicleTypeOptions = [
    { value: 'small_truck', label: p('vehicle_small_truck') },
    { value: 'medium_truck', label: p('vehicle_medium_truck') },
    { value: 'large_truck', label: p('vehicle_large_truck') },
    { value: 'van', label: p('method_van') },
    { value: 'motorcycle', label: p('vehicle_motorcycle') },
    { value: 'bicycle', label: p('vehicle_bicycle') },
    { value: 'car', label: p('method_car') }
  ];

  const auditStatusOptions = [
    { value: 'pending', label: p('audit_pending') },
    { value: 'in_progress', label: p('audit_in_progress') },
    { value: 'completed', label: p('audit_completed') },
    { value: 'approved', label: p('audit_approved') },
    { value: 'rejected', label: p('audit_rejected') }
  ];

  const deliveryFormTabs = useMemo(() => ([
    { id: 'general', label: p('section_basic_info'), icon: Package, iconColor: 'text-blue-400' },
    { id: 'delivery', label: p('section_delivery'), icon: MapPin, iconColor: 'text-emerald-400' },
    { id: 'costs', label: p('section_costs'), icon: DollarSign, iconColor: 'text-green-400' },
  ]), [p]);

  const deliveryTabSectionIds = useMemo(() => ({
    general: ['basicInfo', 'customer'],
    delivery: ['delivery', 'tracking'],
    costs: ['costs', 'notes'],
  }), []);

  const formSections = useMemo(
    () => getDeliveryFormSections({
      statusOptions,
      priorityOptions,
      deliveryTypeOptions,
      zoneOptions,
      paymentMethodOptions,
      paymentStatusOptions,
      vehicleTypeOptions,
      yesNoOptions,
    }),
    [statusOptions, priorityOptions, deliveryTypeOptions, zoneOptions, paymentMethodOptions, paymentStatusOptions, vehicleTypeOptions, yesNoOptions],
  );

  const renderField = (field) => {
    const value = formData[field.key] ?? '';
    const onChange = (v) => setFormData((prev) => ({ ...prev, [field.key]: v }));
    const textareaCls = isLight
      ? 'w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500'
      : 'w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500';

    if (field.type === 'client') {
      const clientOptions = availableClients.map((client) => ({
        value: String(client.id),
        label: `${client.company_name || client.name || p('col_customer')}${client.contact_person ? ` — ${client.contact_person}` : ''}${client.phone1 ? ` — ${client.phone1}` : ''}`,
      }));
      return (
        <CustomSelect
          value={formData.customer_id ? String(formData.customer_id) : ''}
          onChange={(clientId) => {
            if (!clientId) {
              setFormData((prev) => ({
                ...prev,
                customer_id: '',
                customer_name: '',
                customer_phone: '',
                customer_email: '',
              }));
              return;
            }
            const client = availableClients.find((c) => String(c.id) === String(clientId));
            if (client) {
              setFormData((prev) => ({
                ...prev,
                customer_id: String(client.id),
                customer_name: client.company_name || client.name || '',
                customer_phone: client.phone1 || client.phone || '',
                customer_email: client.email || '',
              }));
            }
          }}
          options={clientOptions}
          placeholder={p('placeholder_select_client', { defaultValue: p('col_customer') })}
          disabled={loadingClients}
          searchable
          required={field.required}
        />
      );
    }
    if (field.type === 'select') {
      return (
        <CustomSelect
          value={value}
          onChange={onChange}
          options={field.options || []}
          creatable={false}
          searchable
          placeholder={p('select')}
        />
      );
    }
    if (field.type === 'textarea') {
      return (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className={textareaCls}
        />
      );
    }
    return (
      <input
        type={field.type || 'text'}
        required={field.required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cfgTheme.input}
      />
    );
  };

  const renderViewValue = (record, key) => {
    const raw = record?.[key];
    if (raw == null || raw === '') return p('em_dash');
    if (key === 'status') return getStatusLabel(raw);
    if (key === 'priority') return getPriorityLabel(raw);
    if (key === 'delivery_type') return getTypeLabel(raw);
    if (key === 'zone') return getZoneLabel(raw);
    return String(raw);
  };

  const listStats = useMemo(
    () => mapVentesStatCards([
      { key: 'total', label: p('stat_total'), value: stats.total, icon: Truck, variant: 'copper' },
      { key: 'pending', label: p('stat_pending'), value: stats.pending, icon: Clock, variant: 'forest' },
      { key: 'in_transit', label: p('stat_in_transit'), value: stats.inTransit, icon: Navigation, variant: 'copper' },
      { key: 'delivered', label: p('stat_delivered'), value: stats.delivered, icon: PackageCheck, variant: 'forest' },
    ]),
    [stats, p],
  );

  const advancedOptionsContent = (
    <div className="space-y-6 pt-6">
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
        <div className="flex space-x-1 overflow-x-auto table-scroll">
          {mainTabs.map((tab) => (
            <button key={tab.id} type="button" onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 whitespace-nowrap ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
              <tab.icon className="h-5 w-5 flex-shrink-0" /><span className="hidden sm:inline">{tab.name}</span>
            </button>
          ))}
        </div>
      </div>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
          {activeDistributionFilter && (
            <button type="button" onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
              <XCircle size={14} />{p('reset_filter')}
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
          {distributionInfo.data.map((item) => (
            <button key={item.filterKey || item.name} type="button" onClick={() => setActiveDistributionFilter(activeDistributionFilter === item.filterKey ? null : item.filterKey)} className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === item.filterKey ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`} title={p('filter_by_title', { name: item.name })}>
              <div className="flex justify-between items-center mb-2">
                <h4 className={`font-medium text-sm ${activeDistributionFilter === item.name ? 'text-white' : 'text-white/80'} truncate`}>{item.name}</h4>
                <div className={`text-xs rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold flex-shrink-0 ml-2`}>{item.count}</div>
              </div>
            </button>
          ))}
        </div>
      </motion.div>
    </div>
  );

  return (
    <>
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
      <VentesConfigNotice
        message={!ventesConfig.enableDeliveries ? p('notice_deliveries_disabled', { defaultValue: 'التسليمات معطّلة — فعّلها من إعدادات المبيعات ← التسليمات.' }) : null}
      />
      <VentesListPageChrome
        t={t}
        p={p}
        qawaem={qawaem}
        isDashboardLightMode={isDashboardLightMode}
        title={p('title')}
        subtitle={p('subtitle')}
        icon={Truck}
        listStats={listStats}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        filterActions={buildVentesBulkDeleteFilterAction({
          selectedCount: selectedDeliveries.length,
          onDelete: handleBulkDelete,
          label: p('bulk_delete_confirm', { count: selectedDeliveries.length }),
        })}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={p('search_placeholder', { defaultValue: t('common.search') })}
        onCreate={ventesConfig.enableDeliveries ? openCreateModal : undefined}
        createLabel={p('new_delivery')}
        importExportState={{ showImportDropdown, setShowImportDropdown, showExportDropdown, setShowExportDropdown, importDropdownRef, exportDropdownRef }}
        importExportHandlers={{ onImport: handleImport, onImportCSV: handleImport, onImportExcel: handleImport, onExportCSV: handleExportCSV, onExportExcel: handleExportExcel, onExportPDF: handleExportPDF }}
        showAdvancedOptions={showAdvancedOptions}
        onToggleAdvancedOptions={() => setShowAdvancedOptions(!showAdvancedOptions)}
        advancedOptionsLabel={p('advanced_options')}
        advancedOptionsContent={advancedOptionsContent}
      >
        <div className={facturationTableScrollClass(qawaem)}>
          <table className="w-full employees-table data-table">
            <thead className="glass-table-head">
              <tr>
                <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                  <div className="flex justify-center">
                    <QawaemTableCheckbox
                      size="lg"
                      checked={allRowsSelected}
                      indeterminate={someRowsSelected}
                      onChange={handleSelectAll}
                      title={allRowsSelected ? p('deselect_all', { defaultValue: t('common.deselect_all') }) : p('select_all', { defaultValue: t('common.select_all') })}
                    />
                  </div>
                </th>
                {showCol('delivery_reference') ? (
                  <TableColumnHeaderFilter columnKey="delivery_reference" label={p('col_reference')} value={filters.delivery_reference || ''} onChange={(v) => setFilter('delivery_reference', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                ) : null}
                {showCol('customer_name') ? (
                  <TableColumnHeaderFilter columnKey="customer_name" label={p('col_customer')} value={filters.customer_name || ''} onChange={(v) => setFilter('customer_name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                ) : null}
                {showCol('delivery_date') ? (
                  <TableColumnHeaderFilter columnKey="delivery_date" label={p('col_date')} value={filters.delivery_date || ''} onChange={(v) => setFilter('delivery_date', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                ) : null}
                {showCol('delivery_city') ? (
                  <TableColumnHeaderFilter columnKey="delivery_city" label={p('col_city')} value={filters.delivery_city || ''} onChange={(v) => setFilter('delivery_city', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                ) : null}
                {showCol('status') ? (
                  <TableColumnHeaderFilter columnKey="status" label={p('col_status')} kind="select" options={deliveryStatusFilterOptions} value={filters.status || ''} onChange={(v) => setFilter('status', v)} align="center" editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName={thFilterClass} />
                ) : null}
                <QawaemTableHeaderActionsCell
                  label={p('col_actions')}
                  columns={qawaemColumnDefs}
                  onToggle={handleToggleColumn}
                  visibleCount={qawaemVisibleColumnCount}
                  totalCount={qawaemColumnDefs.length}
                  thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                />
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {displayedDeliveries.length > 0 ? displayedDeliveries.map((d) => (
                <tr key={d.id} className={`hover:bg-white/5 ${selectedDeliveries.includes(d.id) ? 'bg-white/10' : ''}`}>
                  <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        checked={selectedDeliveries.includes(d.id)}
                        onChange={() => handleSelectDelivery(d.id)}
                      />
                    </div>
                  </td>
                  {showCol('delivery_reference') ? (
                    <td className="px-6 py-4 text-white font-semibold">{d.delivery_reference || p('em_dash')}</td>
                  ) : null}
                  {showCol('customer_name') ? (
                    <td className="px-6 py-4 text-white/80">{d.customer_name || p('em_dash')}</td>
                  ) : null}
                  {showCol('delivery_date') ? (
                    <td className="px-6 py-4 text-white/70 text-sm">{d.delivery_date || p('em_dash')}</td>
                  ) : null}
                  {showCol('delivery_city') ? (
                    <td className="px-6 py-4 text-white/70">{d.delivery_city || p('em_dash')}</td>
                  ) : null}
                  {showCol('status') ? (
                    <td className="px-6 py-4 text-center">
                      <div className="flex justify-center">
                        <QawaemClickableStatusBadge
                          status={d.status}
                          recordId={d.id}
                          options={deliveryStatusOptions}
                          onChange={handleStatusChange}
                          title={p('click_change_status', { defaultValue: t('common.change_status') })}
                        />
                      </div>
                    </td>
                  ) : null}
                  <td className="px-6 py-4">
                    <QawaemErpRowActions
                      onView={() => handleView(d)}
                      onEdit={() => handleEdit(d)}
                      onDelete={() => handleDelete(d.id)}
                      viewTitle={t('common.view')}
                      editTitle={t('common.edit')}
                      deleteTitle={t('common.delete')}
                    />
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={tableColSpan} className="text-center py-12 text-white/60">{p('empty')}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </VentesListPageChrome>

      <QawaemErpCrudModal
        open={showModal}
        onClose={resetForm}
        title={editingDelivery ? p('modal_edit') : p('modal_create')}
        icon={Truck}
        showSaveButton
        formId="livraisons-form"
        saveLabel={t('common.save')}
        saving={saving}
        testId="livraisons-crud-modal"
        tabs={deliveryFormTabs}
        activeTab={formTab}
        onTabChange={setFormTab}
        tabsAriaLabel={editingDelivery ? p('modal_edit') : p('modal_create')}
      >
        <form id="livraisons-form" onSubmit={handleSubmit}>
          {deliveryTabSectionIds[formTab]?.map((sectionId) => {
            const section = formSections.find((s) => s.id === sectionId);
            if (!section) return null;
            return (
              <section key={section.id} className={sectionId !== deliveryTabSectionIds[formTab][deliveryTabSectionIds[formTab].length - 1] ? 'mb-6 pb-6 border-b border-white/10' : ''}>
                <h4 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isLight ? 'text-gray-800' : 'text-white/90'}`}>
                  <Package size={16} className="text-blue-400" />
                  {p(section.titleKey)}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {section.fields.map((field) => (
                    <div key={field.key} className={field.fullWidth ? 'md:col-span-3' : ''}>
                      <label className={`block w-full text-sm mb-2 font-medium text-right ${isLight ? 'text-slate-700' : 'text-white/70'}`}>
                        {p(field.labelKey)}
                        {field.required ? <span className="qawaem-required-mark"> *</span> : null}
                      </label>
                      {renderField(field)}
                    </div>
                  ))}
                </div>
              </section>
            );
          })}
        </form>
      </QawaemErpCrudModal>

      {showViewModal && viewingDelivery ? (
      <QawaemErpViewModal
        open
        onClose={() => setShowViewModal(false)}
        title={p('view_title')}
        icon={Eye}
        testId="livraisons-view-modal"
      >
          <div className="space-y-3">
            {DELIVERY_VIEW_COLUMNS.map((col) => (
              <div
                key={col.key}
                className={`flex justify-between gap-4 border-b pb-2 ${isLight ? 'border-slate-100' : 'border-white/5'}`}
              >
                <span className={`text-sm shrink-0 ${isLight ? 'text-slate-500' : 'text-white/50'}`}>
                  {p(col.labelKey)}
                </span>
                <span className={`text-sm text-end ${cfgTheme.title}`}>
                  {renderViewValue(viewingDelivery, col.key)}
                </span>
              </div>
            ))}
          </div>
      </QawaemErpViewModal>
      ) : null}
    </>
  );
};

export default LivraisonsVentesPage;

